import React from 'react';
import { createPortal } from 'react-dom';
import { SlidersHorizontal } from 'lucide-react';
import { AppSelect } from '../../../components/AppSelect';
import { useEscapeClose } from '../../../hooks/useEscapeClose';
import { FILTER_GROUP_LABEL_CLASS } from '../../../ui/controlStyles';
import { SETTINGS_SELECT_TRIGGER_CLASS } from '../../settings/utils/settingsSectionStyles';
import { LEAD_DATE_RANGE_OPTIONS, type LeadDateRange } from '../utils/leadListView';

export const ALL_FILTER_VALUE = 'Alle';
export const NO_CATEGORY_FILTER_VALUE = '__no_category__';

const PANEL_WIDTH = 280;

export type LeadFilterState = {
  category: string;
  status: string;
  dateRange: LeadDateRange;
};

type LeadFilterPopoverProps = {
  categories: Array<{ key: string; label: string }>;
  /** Aus den geladenen Projekten abgeleitet — Phasen sind pro Instanz konfigurierbar. */
  statuses: string[];
  /** Ob es Projekte ohne Kategorie gibt — dann soll „Keine Kategorie“ auswählbar sein. */
  hasNoCategory: boolean;
  value: LeadFilterState;
  onChange: (next: Partial<LeadFilterState>) => void;
  onReset: () => void;
  activeCount: number;
};

export function LeadFilterPopover({
  categories,
  statuses,
  hasNoCategory,
  value,
  onChange,
  onReset,
  activeCount,
}: LeadFilterPopoverProps) {
  const [open, setOpen] = React.useState(false);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const panelRef = React.useRef<HTMLDivElement>(null);
  const [pos, setPos] = React.useState<{ top: number; left: number } | null>(null);

  React.useLayoutEffect(() => {
    if (!open) return;
    const update = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const left = Math.min(Math.max(8, rect.right - PANEL_WIDTH), window.innerWidth - PANEL_WIDTH - 8);
      setPos({ top: rect.bottom + 6, left });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  useEscapeClose(() => setOpen(false), open);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={open}
        className={`inline-flex h-9 shrink-0 items-center gap-2 rounded-md px-3 text-sm font-medium transition-colors ${
          activeCount > 0
            ? 'bg-blue-500/10 text-pipedrive-blue'
            : 'text-app-text-muted hover:bg-app-hover hover:text-app-text'
        }`}
      >
        <SlidersHorizontal className="h-4 w-4" strokeWidth={1.75} />
        Filter
        {activeCount > 0 ? (
          <span className="rounded-full bg-pipedrive-blue px-1.5 text-xs font-semibold tabular-nums text-white">
            {activeCount}
          </span>
        ) : null}
      </button>

      {open && pos
        ? createPortal(
            <div
              ref={panelRef}
              role="dialog"
              aria-label="Projekte filtern"
              style={{ top: pos.top, left: pos.left, width: PANEL_WIDTH }}
              className="fixed z-[80] rounded-lg border border-app-border bg-app-surface p-4 shadow-lg"
            >
              <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-1.5">
                  <span className={FILTER_GROUP_LABEL_CLASS}>Kategorie</span>
                  <AppSelect
                    value={value.category}
                    onChange={(v) => onChange({ category: v })}
                    triggerClassName={SETTINGS_SELECT_TRIGGER_CLASS}
                    aria-label="Nach Kategorie filtern"
                    options={[
                      { value: ALL_FILTER_VALUE, label: 'Alle Kategorien' },
                      ...(hasNoCategory ? [{ value: NO_CATEGORY_FILTER_VALUE, label: 'Keine Kategorie' }] : []),
                      ...categories.map((c) => ({ value: c.key, label: c.label })),
                    ]}
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <span className={FILTER_GROUP_LABEL_CLASS}>Status</span>
                  <AppSelect
                    value={value.status}
                    onChange={(v) => onChange({ status: v })}
                    triggerClassName={SETTINGS_SELECT_TRIGGER_CLASS}
                    aria-label="Nach Status filtern"
                    options={[
                      { value: ALL_FILTER_VALUE, label: 'Alle Status' },
                      ...statuses.map((s) => ({ value: s, label: s })),
                    ]}
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <span className={FILTER_GROUP_LABEL_CLASS}>Erstellt</span>
                  <AppSelect
                    value={value.dateRange}
                    onChange={(v) => onChange({ dateRange: v as LeadDateRange })}
                    triggerClassName={SETTINGS_SELECT_TRIGGER_CLASS}
                    aria-label="Nach Zeitraum filtern"
                    options={LEAD_DATE_RANGE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
                  />
                </div>
              </div>

              <div className="mt-4 flex items-center justify-between">
                <button
                  type="button"
                  onClick={onReset}
                  disabled={activeCount === 0}
                  className="text-sm font-medium text-app-text-subtle transition-colors hover:text-app-text disabled:opacity-40"
                >
                  Zurücksetzen
                </button>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="inline-flex h-9 items-center rounded-md bg-pipedrive-blue px-3.5 text-sm font-semibold text-white transition-colors hover:bg-blue-700"
                >
                  Fertig
                </button>
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
